import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import { Box, Button, Chip, Divider, Grid, Icon, IconButton, Modal, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ModalContext } from "../../../shared/components";
import { globalMaskReal } from "../../../shared/utils";

interface ModalResultAnalysisProps {
	status: string;
	limitSerasa?: number;
	plan: Array<any>;
	propFinally: string;
	chargesBaseCalc: number;
	assuranceBaseCalc: number;
	idcreate: string;
	smDown: boolean;
	isOpenModal: boolean;
	isCloseModal: () => void;
	Rows: any;
}

type PlanCardModel = {
	id: string;
	name: string;
	price: number;
	priceTotal: number;
	fee: number;
	lmiMultiplier: number;
	lmiValue: number;
	isChargesPlan: boolean;
	bonusAmount?: string;
	features: string[];
	originalRow: any;
};

const formatBRL = (value: number) => value.toLocaleString("pt-br", { style: "currency", currency: "BRL" });

const ModalResultAnalysisAll: React.FC<ModalResultAnalysisProps> = ({
	status,
	plan,
	propFinally,
	chargesBaseCalc,
	assuranceBaseCalc,
	idcreate,
	smDown,
	isOpenModal,
	isCloseModal,
	Rows,
}) => {
	const [limitSerasa, setLimitSerasa] = React.useState<string>("0");
	const navigate = useNavigate();

	const isApprovedView = status !== "refused" && status !== "pending";

	const title =
		status === "issued"
			? "Planos Aprovados"
			: status === "awaiting_biometria"
			? "Aprovada Aguardando Biometria"
			: status === "pre_approved" && (limitSerasa ?? "0") !== "0"
			? "Análise Pré - Aprovada"
			: status === "pending"
			? "Análise Pendente"
			: "Análise Recusada";

	useEffect(() => {
		if (isOpenModal && Rows && Rows.creditLimit != null) {
			setLimitSerasa(Rows.creditLimit.toString());
			return;
		}
		if (isOpenModal) {
			setLimitSerasa("0");
		}
	}, [isOpenModal, Rows]);

	const mapRowToCard = (row: any): PlanCardModel => {
		const baseAmount = row?.planId?.includes("__charges") ? chargesBaseCalc : assuranceBaseCalc;
		const fee = parseFloat((propFinally === "residential" ? row?.residentialFee : row?.commercialFee) || "0");
		const lmiMultiplier = parseFloat(row?.lmi || "0");
		const price = baseAmount * (fee / 100);
		const priceTotal = price * 12;
		const lmiValue = baseAmount * lmiMultiplier;
		const bonusAmount = row?.bonusAmount ?? row?.plan?.bonusAmount;

		return {
			id: String(row?.id ?? row?.planId ?? Math.random()),
			name: row?.planId?.includes("__charges") ? `${row?.plan?.description ?? ""} +` : row?.plan?.description ?? "",
			price,
			priceTotal,
			fee,
			lmiMultiplier,
			lmiValue,
			isChargesPlan: Boolean(row?.planId?.includes("__charges")),
			bonusAmount: bonusAmount != null ? String(bonusAmount) : undefined,
			// Mantém idêntico ao ModalApprovedV2
			features: ["Sem fiador", "Assessoria Jurídica", "100% Digital"],
			originalRow: row,
		};
	};

	const handlePlanAction = (row: any) => {
		if (status === "issued") {
			navigate(`/analises/contratacao?analise=${idcreate}&plano=${row.planId}`);
			return;
		}
		navigate("/analises/");
	};

	if (!isApprovedView) {
		const messageType = status === "pending" ? "info" : "error";
		const message =
			status === "pending"
				? "Sua análise está em processamento. Aguarde a finalização e tente novamente em alguns instantes."
				: "Sua análise foi recusada. Caso precise, revise os dados e tente novamente.";

		return (
			<ModalContext
				isCloseModal={isCloseModal}
				isOpenModal={isOpenModal}
				messageType={messageType}
				showButtonConfirm
				showButtonCancel={false}
				textButtonConfirm="Fechar"
				onclickConfirm={isCloseModal}
				ITitle={title}
				IMessage={[
					<Box key="msg" sx={{ width: "100%", maxWidth: 560, p: 1 }}>
						<Typography variant={smDown ? "body1" : "h6"} sx={{ fontWeight: 400, textAlign: "center" }}>
							{message}
						</Typography>
					</Box>,
				]}
			/>
		);
	}

	const activePlans = (plan || []).filter((p: any) => {
		const planStatus = String(p?.status ?? p?.plan?.status ?? "").toLowerCase();
		return planStatus === "active";
	});

	const cards = activePlans.map(mapRowToCard);

	return (
		<Modal
			open={isOpenModal}
			disableEscapeKeyDown
			onClose={(_, reason) => {
				if (reason !== "backdropClick") {
					isCloseModal();
				}
			}}
		>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "#FFFF",
					boxShadow: 24,
					width: "95%",
					maxWidth: 1000,
					maxHeight: "90vh",
					borderRadius: 3,
					outline: "none",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
					<Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
						<Typography variant={smDown ? "h5" : "h4"} sx={{ color: "#38aa2eff", fontWeight: 300 }}>
							{title}
						</Typography>
						<IconButton onClick={isCloseModal} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>

					{status === "pre_approved" && limitSerasa.toString() !== "0" && (
						<Box sx={{ mb: 2 }}>
							<Typography variant="h6" color="primary">
								{`Limite de crédito: R$ ${globalMaskReal(limitSerasa)}`}
							</Typography>
						</Box>
					)}
				</Box>

				<Box sx={{ p: 3, pt: 0, flex: 1, overflowY: "auto" }}>
					{cards.length === 0 ? (
						<Typography variant="body1" color="text.secondary">
							Nenhum plano disponível para exibição.
						</Typography>
					) : (
						cards.map((c) => (
							<Box
								key={c.id}
								sx={{
									bgcolor: "#fff",
									borderRadius: 4,
									boxShadow: "rgba(17, 17, 26, 0.1) 0px 4px 16px, rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px;",
									overflow: "hidden",
									border: "1px solid #f0f0f0",
									mb: 3,
									display: "flex",
									flexDirection: "column",
								}}
							>
								<Grid container sx={{ flex: 1 }}>
									<Grid
										item
										xs={12}
										md={3}
										sx={{
											p: 2,
											background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
											color: "#fff",
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
										}}
									>
										<Stack spacing={1} alignItems="flex-start">
											{c.bonusAmount && parseFloat(c.bonusAmount) > 0 && (
												<Chip
													icon={<PaidIcon sx={{ fontSize: "16px !important", color: "#7B1FA2 !important" }} />}
													label={`Bônus: ${parseFloat(c.bonusAmount).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}`}
													size="small"
													sx={{
														bgcolor: "#fff",
														color: "#7B1FA2",
														fontWeight: 700,
														fontSize: "0.75rem",
														height: 24,
														mb: 0.5,
														boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
													}}
												/>
											)}
											<Typography
												variant="h4"
												sx={{
													color: "#fff",
													fontWeight: 800,
													fontSize: "1.6rem",
													textShadow: "0px 2px 4px rgba(0,0,0,0.1)",
												}}
											>
												{c.name}
											</Typography>
											<Box>
												<Typography component="span" sx={{ color: "#fff", fontWeight: 700, fontSize: "1.5rem" }}>
													{formatBRL(c.price)}
												</Typography>
												<Typography component="span" sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.9rem", ml: 0.5 }}>
													/mês
												</Typography>
											</Box>
											<Box
												sx={{
													bgcolor: "rgba(0, 0, 0, 0.15)",
													px: 1.5,
													py: 0.5,
													borderRadius: 50,
													display: "inline-block",
													mt: 1,
												}}
											>
												<Typography variant="caption" sx={{ color: "#fff", fontWeight: 500 }}>
													Total Anual: {formatBRL(c.priceTotal)}
												</Typography>
											</Box>
										</Stack>
									</Grid>

									<Grid item xs={12} md={6} sx={{ p: 2 }}>
										<Stack spacing={1.5}>
											<Box sx={{ bgcolor: "#F8F9FE", borderRadius: 3, p: 1.5 }}>
												<Stack
													direction="row"
													divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "#DDE3F2" }} />}
													justifyContent="space-around"
													alignItems="center"
													spacing={2}
												>
													<Stack alignItems="center" spacing={0.5}>
														<Icon
															sx={{
																background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
																WebkitBackgroundClip: "text",
																WebkitTextFillColor: "transparent",
																fontSize: 32,
																mb: 0.5,
															}}
														>
															percent
														</Icon>
														<Typography variant="caption" color="text.secondary" fontWeight={500}>
															Taxa Garantti
														</Typography>
														<Typography variant="subtitle1" fontWeight={800} color="#344767">
															{c.fee.toFixed(2)}%
														</Typography>
													</Stack>

													<Stack alignItems="center" spacing={0.5}>
														<Icon
															sx={{
																background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
																WebkitBackgroundClip: "text",
																WebkitTextFillColor: "transparent",
																fontSize: 32,
																mb: 0.5,
															}}
														>
															home
														</Icon>
														<Typography variant="caption" color="text.secondary" fontWeight={500}>
															LMI
														</Typography>
														<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
															<Typography variant="subtitle1" fontWeight={800} color="#344767" sx={{ lineHeight: 1.2 }}>
																{c.lmiMultiplier}x
															</Typography>
															<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
																({c.isChargesPlan ? "Aluguel + Encargos" : "Aluguel"})
															</Typography>
														</Box>
													</Stack>

													<Stack alignItems="center" spacing={0.5}>
														<Icon
															sx={{
																background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
																WebkitBackgroundClip: "text",
																WebkitTextFillColor: "transparent",
																fontSize: 32,
																mb: 0.5,
															}}
														>
															payments
														</Icon>
														<Typography variant="caption" color="text.secondary" fontWeight={500}>
															Valor LMI
														</Typography>
														<Typography variant="subtitle1" fontWeight={800} color="#344767">
															{formatBRL(c.lmiValue)}
														</Typography>
													</Stack>
												</Stack>
											</Box>

											<Divider />

											<Stack direction="row" spacing={3} flexWrap="wrap">
												{c.features.map((feature, index) => (
													<Stack key={index} direction="row" spacing={0.5} alignItems="center" sx={{ mr: 2, mb: 1 }}>
														<CheckIcon sx={{ color: "#4caf50" }} fontSize="small" />
														<Typography variant="body2" fontWeight="500">
															{feature}
														</Typography>
													</Stack>
												))}
											</Stack>
										</Stack>
									</Grid>

									<Grid
										item
										xs={12}
										md={3}
										sx={{
											p: 2,
											borderLeft: { md: "1px solid #f0f0f0" },
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
										}}
									>
										<Button
											variant="contained"
											color="primary"
											size="large"
											endIcon={<ArrowForwardIcon />}
											onClick={() => handlePlanAction(c.originalRow)}
											sx={{ borderRadius: 50, px: 2.5, py: 1, fontWeight: 700, width: "100%", fontSize: "1.1rem" }}
										>
											{status === "issued" ? "CONTRATAR" : "AGUARDANDO BIOMETRIA"}
										</Button>
									</Grid>
								</Grid>
							</Box>
						))
					)}
				</Box>
			</Box>
		</Modal>
	);
};

export default ModalResultAnalysisAll;
